/**
 * What a school switch does to the TanStack cache. The only code allowed to
 * sweep the whole cache (`lib/query/keys/README.md`).
 *
 * Two kinds of key behave differently:
 *
 * - **Scoped** (`['school', id, …]`, `['global', …]`): the request sends the
 *   school in its key, or no school at all, so the new school is simply a new
 *   key. The old school's entries are kept, marked stale.
 * - **Legacy** (everything else, plus the pre-plan `['school', id, …]` roots in
 *   `UNVERIFIED_SCOPED_DOMAINS`): the request reads the active-school cookie at
 *   send time, so a fetch that runs under an old key after the cookie flips would
 *   cache school B's data under school A's key. Their data is cleared, and they
 *   refetch only once every observer has moved to its new key.
 *
 * `SchoolProvider` calls these in order: `cancelForSchoolChange` before it
 * writes the new school, `onActiveSchoolChanged` right after the cookie writes,
 * and `refetchLegacyAfterSchoolChange` from an effect once the new school has
 * rendered. Why not `resetQueries`: it refetches active queries at once, while
 * mounted observers still sit on their old keys and the cookie already says B.
 */

import type { Query, QueryClient, QueryKey } from '@tanstack/react-query'

import { isScopedKey, keyStartsWith, schoolScope } from '@/lib/query/scope'

/**
 * Domain segment (third position) of every `['school', id, '<domain>', …]` key
 * that predates the factories and whose request has NOT been shown to send that
 * same `schoolId` (as the `backend*` `schoolId` override or the backend client's
 * `schoolId`). These put the school in the URL but let the header fall back to
 * the cookie, so the switch treats them as legacy.
 *
 * Shrink-only: the domain wave (or sibling plan) that makes a domain's requests
 * send the key's school removes its entry. Never add one; a new key comes from a
 * factory and sends its key's school.
 */
export const UNVERIFIED_SCOPED_DOMAINS: readonly string[] = [
  'mobile-app', // lib/api/school-mobile-app.ts, lib/hooks/use-school-mobile-app.ts
  'mobile-app-screens', // lib/api/mobile-app-screens.ts
  'resolve-entitlements', // lib/hooks/use-subscriptions.ts → lib/api/subscriptions.ts
  'subscriptions' // lib/hooks/use-subscriptions.ts → lib/api/subscriptions.ts
]

/** True when the key's request may read the active-school cookie at send time. */
export function isLegacyKey(key: QueryKey): boolean {
  if (!isScopedKey(key)) return true
  const domain = key[2]
  return key[0] === 'school' && typeof domain === 'string' && UNVERIFIED_SCOPED_DOMAINS.includes(domain)
}

const isLegacyQuery = (query: Query) => isLegacyKey(query.queryKey)

/**
 * Step 1, called BEFORE the new school is written anywhere: stop every legacy
 * fetch and every fetch for the previous school, so none of them can read the
 * new cookie and land under an old key. The cancel itself is synchronous
 * (`Query.cancel` rejects the in-flight fetch at once); the returned promise
 * only settles the bookkeeping, so callers need not await it.
 */
export function cancelForSchoolChange(queryClient: QueryClient, previousSchoolId: string | null): Promise<void> {
  const previousRoot = previousSchoolId === null ? null : schoolScope(previousSchoolId)
  return queryClient.cancelQueries({
    predicate: query => isLegacyQuery(query) || (previousRoot !== null && keyStartsWith(query.queryKey, previousRoot))
  })
}

/**
 * Step 2, synchronous, right after the cookie writes and before any await:
 * clear legacy data without refetching it, mark the previous school stale, and
 * retry every errored query.
 */
export function onActiveSchoolChanged(queryClient: QueryClient, previousSchoolId: string | null): void {
  // `Query.reset()` drops the data and fires no fetch, so a legacy screen shows
  // a loading state rather than school A's rows under school B's name, and no
  // old key can fetch with the new cookie before its observer re-renders.
  queryClient
    .getQueryCache()
    .findAll({ predicate: isLegacyQuery })
    .forEach(query => query.reset())

  // Switching back renders the old school's cache at once and refetches it in
  // the background.
  if (previousSchoolId !== null) {
    void queryClient.invalidateQueries({ queryKey: schoolScope(previousSchoolId), refetchType: 'none' })
  }

  // The 428 recovery: queries that failed before a school cookie existed stay
  // settled in error under a key that does not change. Legacy ones were just
  // reset; scoped ones send their key's school; global ones ignore the school.
  void queryClient.invalidateQueries({ predicate: query => query.state.status === 'error' })
}

/**
 * Step 3, from a `SchoolProvider` effect after the switch has rendered: every
 * mounted observer is on its new key by then, so refetching the active legacy
 * queries cannot cache B's data under an A key. `cancelRefetch: false` joins the
 * fetches the new keys already started on mount instead of restarting them.
 */
export function refetchLegacyAfterSchoolChange(queryClient: QueryClient): Promise<void> {
  return queryClient.refetchQueries({ predicate: isLegacyQuery, type: 'active' }, { cancelRefetch: false })
}
